import { useMemo, useState } from "react";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Link } from "@tanstack/react-router";
import { ExternalLink, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ConceptMultiSelect, OrganizationSelect } from "@/components/common/entity-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppDialog } from "@/components/common/AppDialog";
import { formatCurrency } from "@/components/recipes/cost/CostValue";
import { useRecipes, useCostSummary } from "@/hooks/recipes";
import { useMenusForItems, useCreateMenu } from "@/hooks/menu-items";
import { useMenuCategories, useCreateMenuCategory } from "@/hooks/menu-categories";
import { useSoleActiveConcept } from "@/hooks/useSoleActiveConcept";
import { getApiErrorMessage, isConflictError } from "@/lib/api/api";
import type { MenuType } from "@/lib/api/requests/menu-items";

const MENU_TYPES: MenuType[] = [
  "ALL_DAY",
  "BREAKFAST",
  "LUNCH",
  "DINNER",
  "SEASONAL",
  "SPECIAL",
];

const MENU_TYPE_LABELS: Record<MenuType, MessageDescriptor> = {
  ALL_DAY: msg`All day`,
  BREAKFAST: msg`Breakfast`,
  LUNCH: msg`Lunch`,
  DINNER: msg`Dinner`,
  SEASONAL: msg`Seasonal`,
  SPECIAL: msg`Special`,
};

/** Sentinel value for the "create new menu" entry inside the Menu dropdown. */
const CREATE_MENU_VALUE = "__create_menu__";

/** Sentinel value for the "add category" entry inside the Category dropdown. */
const CREATE_CATEGORY_VALUE = "__create_category__";

/** The one plated recipe a menu item serves (T-904). */
export interface MenuItemFormDish {
  recipeId: number;
  name: string;
  /** Recipe's target selling price — seeds the menu item price. */
  unitPrice?: number | null;
}

export interface MenuItemFormData {
  name: string;
  menuId: number;
  price?: number;
  /** A category of one of the item's concepts. Required: the form will not save without one. */
  categoryId: number;
  isAvailable: boolean;
  /**
   * A one-element array: the relation underneath is still many-to-many, but a
   * menu item links exactly one recipe and the API enforces it (T-904).
   */
  recipes: { recipeId: number; quantity: number }[];
  /**
   * The concepts that serve this item — one or more of the organization's. The
   * link belongs to the item, not to its menu: a menu shared org-wide names no
   * concept, so its items would otherwise belong to none either.
   */
  conceptIds: number[];
}

export interface MenuItemInitialValue {
  id: number;
  name: string;
  menuId: number;
  price?: number | null;
  /** The category the item is listed under now. */
  categoryId?: number | null;
  /** That category's name, for showing it while the concept's list loads. */
  category?: string | null;
  isAvailable?: boolean;
  dish?: MenuItemFormDish;
  /** Concepts already serving this item. Seeds the concept selection. */
  conceptIds?: number[];
}


interface CreateMenuItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the dialog edits an existing menu item. */
  initial?: MenuItemInitialValue;
  /**
   * Category a newly created item lands in. Set when the create is started from
   * the list filtered to one, so the category the user was looking at is not
   * asked for again. Ignored when editing — the item's own category wins.
   */
  defaultCategoryId?: number;
  /** Concept the user has no choice about. Hides the concept select entirely. */
  companyId?: number;
  /**
   * Concept the select opens on while staying changeable. A user who can filter
   * across concepts has picked one on the page behind this dialog; that is a
   * starting point, not a lock, so it must not arrive as `companyId`.
   */
  defaultCompanyId?: number | null;
  organizationId?: number | null;
  canSelectOrganization?: boolean;
  onSave: (data: MenuItemFormData) => void;
  isPending?: boolean;
  /**
   * Offered when the dish the user wants isn't in the list: a menu item can
   * only link a plated recipe that already exists.
   */
  onCreatePlatedRecipe?: () => void;
}

/**
 * Wrapper that remounts the form whenever the dialog opens or the edited item
 * changes, so the form re-initialises from `initial` via useState initialisers
 * (no setState-in-effect).
 */
export function CreateMenuItemDialog(props: CreateMenuItemDialogProps) {
  return (
    <MenuItemForm
      key={`${props.open ? "open" : "closed"}:${props.initial?.id ?? "new"}:${props.defaultCategoryId ?? ""}`}
      {...props}
    />
  );
}

function MenuItemForm({
  open,
  onOpenChange,
  initial,
  defaultCategoryId,
  companyId,
  defaultCompanyId,
  organizationId,
  canSelectOrganization = false,
  onSave,
  isPending = false,
  onCreatePlatedRecipe,
}: CreateMenuItemDialogProps) {
  const { i18n } = useLingui();

  const [name, setName] = useState(initial?.name ?? "");
  const [menuId, setMenuId] = useState<string>(
    initial?.menuId != null ? String(initial.menuId) : "",
  );
  const [categoryId, setCategoryId] = useState<string>(
    initial?.categoryId != null
      ? String(initial.categoryId)
      : initial
        ? ""
        : defaultCategoryId != null
          ? String(defaultCategoryId)
          : "",
  );
  const [dish, setDish] = useState<MenuItemFormDish | null>(initial?.dish ?? null);
  const [isAvailable, setIsAvailable] = useState(initial?.isAvailable ?? true);
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Price defaults to the dish's own target selling price, but can be manually
  // overridden. An existing item whose stored price differs from the dish's
  // opens as a manual override so we don't silently change it.
  const initialRollup = initial?.dish?.unitPrice ?? 0;
  const [priceEdited, setPriceEdited] = useState(
    initial?.price != null && Math.abs(Number(initial.price) - initialRollup) > 0.001,
  );
  const [priceInput, setPriceInput] = useState<string>(
    initial?.price != null ? String(initial.price) : "",
  );

  // Inline "create menu" sub-form.
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [newMenuName, setNewMenuName] = useState("");
  // Inline "add category" sub-form.
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryError, setNewCategoryError] = useState<string | null>(null);
  const [newMenuType, setNewMenuType] = useState<MenuType>("ALL_DAY");
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(
    organizationId ?? null,
  );
  const [selectedConceptIds, setSelectedConceptIds] = useState<number[]>(
    initial?.conceptIds?.length
      ? initial.conceptIds
      : companyId != null
        ? [companyId]
        : defaultCompanyId != null
          ? [defaultCompanyId]
          : [],
  );

  // With exactly one active concept there is nothing to choose, so the field
  // arrives pre-filled instead of blocking the save on a foregone conclusion
  // (T-902). With several — or none — it stays required and empty.
  const conceptScopeId = selectedOrganizationId ?? organizationId ?? null;
  const { concept: soleConcept } = useSoleActiveConcept({
    // Only worth asking while the field is on screen, and for internal users
    // only once they have narrowed to an organization — the same condition
    // that ungreys the select below.
    enabled: open && !companyId && (!canSelectOrganization || conceptScopeId != null),
    organizationId: conceptScopeId ?? undefined,
  });
  // Which scope has already been pre-filled. Without this, clearing the field
  // (or picking the other concept after the org changed) would be undone on
  // the next render. Adopting the value during render rather than in an effect
  // keeps this off the setState-in-effect path.
  const [autoFilledScope, setAutoFilledScope] = useState<number | null | undefined>(undefined);
  if (soleConcept && selectedConceptIds.length === 0 && autoFilledScope !== conceptScopeId) {
    setAutoFilledScope(conceptScopeId);
    setSelectedConceptIds([soleConcept.id]);
  }

  // The first concept picked also scopes the rest of the form — which menus,
  // categories and recipes are on offer. They are org-level lists filtered by
  // concept, so one has to lead; the item still saves against the whole set.
  const effectiveCompanyId = companyId ?? selectedConceptIds[0] ?? undefined;

  const { data: menusData } = useMenusForItems(open, effectiveCompanyId);
  const menus = menusData?.data ?? [];

  // A category belongs to a concept, so the list follows the one chosen here.
  const { data: conceptCategories = [] } = useMenuCategories(effectiveCompanyId, open);
  // An item being edited keeps showing its own category even when it belongs to
  // one of its other concepts, which this list (the first concept's) lacks.
  const keptCategory =
    initial?.categoryId != null && initial.category
      ? { id: initial.categoryId, name: initial.category }
      : null;
  const categories =
    keptCategory && !conceptCategories.some((c) => c.id === keptCategory.id)
      ? [...conceptCategories, keptCategory]
      : conceptCategories;
  const createCategory = useCreateMenuCategory();
  const handleCreateCategory = () => {
    const name = newCategoryName.trim();
    if (!effectiveCompanyId) {
      setNewCategoryError(i18n._("Select a concept before adding a category"));
      return;
    }
    if (!name) {
      setNewCategoryError(i18n._("Category name is required"));
      return;
    }
    createCategory.mutate(
      { companyId: effectiveCompanyId, name },
      {
        onSuccess: (created) => {
          setCategoryId(String(created.id));
          setShowNewCategory(false);
          setNewCategoryName("");
          setNewCategoryError(null);
          setError(null);
        },
        onError: (err) =>
          setNewCategoryError(
            isConflictError(err)
              ? i18n._("A category with this name already exists in this concept")
              : getApiErrorMessage(err, i18n._("Failed to add the category")),
          ),
      },
    );
  };

  // Menu item price comes from the dish's own target selling price.
  const computedPrice = dish?.unitPrice ?? 0;
  // A dish with no target selling price would show as $0.00, which is how an
  // unpriced item reached a ticket (T-901) — so the auto total isn't trusted.
  const hasUnpricedRecipe = dish != null && dish.unitPrice == null;
  // What the field shows: the manual override while editing, otherwise the
  // dish's price (so it tracks a change of dish until the user overrides it).
  const priceValue = priceEdited ? priceInput : computedPrice.toFixed(2);

  const createMenu = useCreateMenu();
  const handleCreateMenu = () => {
    if (!effectiveCompanyId) {
      setError(i18n._("Select a concept before creating a menu"));
      return;
    }

    if (!newMenuName.trim()) {
      setError(i18n._("Menu name is required"));
      return;
    }
    createMenu.mutate(
      { name: newMenuName.trim(), type: newMenuType, companyId: effectiveCompanyId },
      {
        onSuccess: (menu) => {
          setMenuId(String(menu.id));
          setShowNewMenu(false);
          setNewMenuName("");
          setError(null);
        },
        onError: (err) =>
          setError(getApiErrorMessage(err, i18n._("Failed to create menu"))),
      },
    );
  };

  // Only plated recipes are offered: a menu item is a dish, and a prep recipe
  // is a component of one. Filtering here means the mistake can't be made,
  // rather than being caught by the API after the save (T-904).
  // Scoped to the selected concept too — otherwise an internal/org-admin user
  // (whose recipe access is org-wide, not concept-scoped) could link a dish
  // that belongs to a different concept than the menu it's being added to.
  // Listed before anything is typed: a picker that stays blank until you
  // guess a name reads as "there are no plated recipes". Search narrows it.
  const { data: recipeResults } = useRecipes(
    {
      search: search.trim() || undefined,
      limit: 50,
      isActive: true,
      recipeType: "PLATED",
      companyId: effectiveCompanyId ?? undefined,
      // `menuItem` says which plated recipes another menu item already serves.
      include: ["cost", "menuItem"],
    },
    // Not before a concept is chosen: without one the list would be every
    // plated recipe the user can see, across concepts.
    open && !dish && effectiveCompanyId != null,
  );
  const platedRecipes = useMemo(
    () => (recipeResults?.data ?? []).filter((r) => r.id !== dish?.recipeId),
    [recipeResults, dish],
  );
  const searchResults = platedRecipes;

  // Food cost of the chosen dish against the price in the field. The cost is
  // the recipe's own per-portion cost, so it is only as good as the recipe's
  // costing: an incomplete one is labelled a minimum rather than passed off.
  const { data: dishCost } = useCostSummary(dish?.recipeId ?? 0, open && dish != null);
  const priceNumber = Number(priceValue);
  const foodCostPercent =
    dishCost?.costPerPortion != null && priceNumber > 0
      ? (dishCost.costPerPortion / priceNumber) * 100
      : null;

  const chooseDish = (recipeId: number, recipeName: string, unitPrice?: number | null) => {
    setDish({ recipeId, name: recipeName, unitPrice });
    setSearch("");
    setError(null);
  };

  const handleSave = () => {
    if (!effectiveCompanyId) {
      setError(i18n._("Select a concept before creating a menu item"));
      return;
    }

    if (!name.trim()) {
      setError(i18n._("Menu item name is required"));
      return;
    }
    if (!menuId) {
      setError(i18n._("Please select a menu"));
      return;
    }
    if (!categoryId) {
      setError(i18n._("Category is required"));
      return;
    }
    if (!dish) {
      setError(i18n._("Select the plated recipe this item serves"));
      return;
    }
    let finalPrice: number | undefined;
    if (priceEdited) {
      const parsed = priceInput.trim() === "" ? NaN : Number(priceInput);
      if (Number.isNaN(parsed) || parsed < 0) {
        setError(i18n._("Price must be a non-negative number"));
        return;
      }
      finalPrice = parsed;
    } else if (isAvailable) {
      // Using the dish's price: refuse to silently save a $0.00 total because
      // the recipe has no price of its own — the user must set one manually.
      // Only enforced while the item is available; an unavailable item is
      // allowed to stay unpriced (matches the API's `assertPricedIfAvailable`).
      if (hasUnpricedRecipe) {
        setError(
          i18n._("This recipe has no price. Set the menu item price manually."),
        );
        return;
      }
      finalPrice = computedPrice;
    } else {
      finalPrice = hasUnpricedRecipe ? undefined : computedPrice;
    }
    // Round to whole cents so the saved value matches what the field displays.
    finalPrice = finalPrice != null ? Math.round(finalPrice * 100) / 100 : undefined;
    onSave({
      name: name.trim(),
      menuId: Number(menuId),
      price: finalPrice,
      categoryId: Number(categoryId),
      isAvailable,
      recipes: [{ recipeId: dish.recipeId, quantity: 1 }],
      // A fixed concept (the dialog opened inside one) is the whole set; there
      // is no picker to widen it.
      conceptIds: companyId != null ? [companyId] : selectedConceptIds,
    });
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={initial ? i18n._("Edit menu item") : i18n._("Create menu item")}
      description={i18n._("A menu item is one dish: name it, price it, and link the plated recipe it serves.")}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-xs text-(--color-danger-text)">{error ?? ""}</span>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {i18n._("Cancel")}
            </Button>
            <Button onClick={handleSave} disabled={isPending}>
              {initial ? i18n._("Save changes") : i18n._("Create menu item")}
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        {(canSelectOrganization || !companyId) && (
          <>
            {canSelectOrganization && (
              <div className="space-y-2">
                <Label>{i18n._("Organization")}</Label>
                <OrganizationSelect
                  value={selectedOrganizationId}
                  adminScope
                  onChange={(value) => {
                    setSelectedOrganizationId(value);
                    setSelectedConceptIds([]);
                    setMenuId("");
                    setCategoryId("");
                    setShowNewMenu(false);
                    setShowNewCategory(false);
                    setError(null);
                  }}
                  onClear={() => {
                    setSelectedOrganizationId(null);
                    setSelectedConceptIds([]);
                    setMenuId("");
                    setCategoryId("");
                  }}
                  placeholder={i18n._("Select organization")}
                />
              </div>
            )}

            {!companyId && (
              <div className="space-y-2">
                <Label>{i18n._("Concepts")}</Label>
                <ConceptMultiSelect
                  value={selectedConceptIds}
                  organizationId={selectedOrganizationId ?? organizationId ?? undefined}
                  // The pre-filled concept is already in hand, so name it here
                  // rather than making the select re-fetch it by id.
                  displayNames={
                    soleConcept ? { [soleConcept.id]: soleConcept.name } : undefined
                  }
                  disabled={canSelectOrganization && !selectedOrganizationId}
                  onChange={(value) => {
                    // The first concept scopes the menu and category lists, so they
                    // only have to be re-picked when that one changes — toggling
                    // a second concept on leaves them valid.
                    if (value[0] !== selectedConceptIds[0]) {
                      setMenuId("");
                      setCategoryId("");
                      setShowNewMenu(false);
                      setShowNewCategory(false);
                    }
                    setSelectedConceptIds(value);
                    setError(null);
                  }}
                  placeholder={i18n._("Select concept")}
                />
              </div>
            )}
          </>
        )}

        <div className="space-y-2">
          <Label htmlFor="mi-name">{i18n._("Name")}</Label>
          <Input
            id="mi-name"
            placeholder={i18n._("e.g. Burger Combo")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>{i18n._("Menu")}</Label>
          <Select
            value={menuId}
            onValueChange={(v) => {
              if (v === CREATE_MENU_VALUE) {
                setShowNewMenu(true);
                setError(null);
              } else {
                setMenuId(v);
                setShowNewMenu(false);
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={i18n._("Select a menu")} />
            </SelectTrigger>
            <SelectContent>
              {menus.map((m) => (
                <SelectItem key={m.id} value={String(m.id)}>
                  {m.name}
                </SelectItem>
              ))}
              <SelectItem value={CREATE_MENU_VALUE} className="text-(--color-action-fg)">
                {i18n._("+ Create new menu")}
              </SelectItem>
            </SelectContent>
          </Select>

          {showNewMenu && (
            <div className="space-y-2 rounded-lg border border-(--color-border) p-2">
              <Input
                placeholder={i18n._("New menu name")}
                value={newMenuName}
                onChange={(e) => setNewMenuName(e.target.value)}
              />
              <Select value={newMenuType} onValueChange={(v) => setNewMenuType(v as MenuType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MENU_TYPES.map((mt) => (
                    <SelectItem key={mt} value={mt}>
                      {i18n._(MENU_TYPE_LABELS[mt])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* The dialog's footer error is easy to miss once this box has
                  pushed the rest of the form below the fold, so a failed
                  "Create menu" (empty name, duplicate name, no concept) has to
                  say so right here too, not just far below. */}
              {error && (
                <p className="text-xs text-(--color-danger-text)">{error}</p>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setShowNewMenu(false);
                    setError(null);
                  }}
                >
                  {i18n._("Cancel")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="flex-1"
                  onClick={handleCreateMenu}
                  disabled={createMenu.isPending}
                >
                  {i18n._("Create menu")}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="mi-price">{i18n._("Price")}</Label>
            {priceEdited && (
              <button
                type="button"
                className="text-xs text-(--color-action-fg) hover:underline"
                onClick={() => {
                  setPriceEdited(false);
                  setPriceInput("");
                }}
              >
                {i18n._("Reset to calculated")}
              </button>
            )}
          </div>
          <Input
            id="mi-price"
            type="number"
            min={0}
            step="0.01"
            placeholder={i18n._("0.00")}
            value={priceValue}
            onChange={(e) => {
              setPriceEdited(true);
              setPriceInput(e.target.value);
            }}
            aria-describedby="mi-price-hint"
          />
          <p id="mi-price-hint" className="text-xs text-(--color-text-secondary)">
            {priceEdited
              ? i18n._("Manually set. Reset to use the recipe's price.")
              : i18n._("Taken from the linked recipe — edit to override.")}
          </p>
          {foodCostPercent != null && (
            <p className="text-xs text-(--color-text-secondary)">
              {dishCost?.isComplete === false
                ? i18n._("Food cost at least {percent}%", { percent: foodCostPercent.toFixed(1) })
                : i18n._("Food cost {percent}%", { percent: foodCostPercent.toFixed(1) })}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="mi-category">{i18n._("Category")}</Label>
          <Select
            value={categoryId}
            onValueChange={(v) => {
              if (v === CREATE_CATEGORY_VALUE) {
                setShowNewCategory(true);
                setNewCategoryError(null);
                setError(null);
              } else {
                setCategoryId(v);
                setShowNewCategory(false);
              }
            }}
          >
            <SelectTrigger id="mi-category" aria-required="true">
              <SelectValue placeholder={i18n._("Select a category")} />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name}
                </SelectItem>
              ))}
              <SelectItem value={CREATE_CATEGORY_VALUE} className="text-(--color-action-fg)">
                {i18n._("+ Add category")}
              </SelectItem>
            </SelectContent>
          </Select>

          {showNewCategory && (
            <div className="space-y-2 rounded-lg border border-(--color-border) p-2">
              <Input
                placeholder={i18n._("New category name")}
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
              />
              {newCategoryError && (
                <p className="text-xs text-(--color-danger-text)">{newCategoryError}</p>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setShowNewCategory(false);
                    setNewCategoryError(null);
                  }}
                >
                  {i18n._("Cancel")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="flex-1"
                  onClick={handleCreateCategory}
                  disabled={createCategory.isPending}
                >
                  {i18n._("Add category")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-(--color-border) px-3 py-2">
        <div className="space-y-0.5">
          <Label htmlFor="mi-available">{i18n._("Available")}</Label>
          <p className="text-xs text-(--color-text-secondary)">
            {i18n._("Unavailable items stay off the menu and may be left unpriced.")}
          </p>
        </div>
        <Switch id="mi-available" checked={isAvailable} onCheckedChange={setIsAvailable} />
      </div>

      <div className="mt-4 space-y-2">
        <Label>{i18n._("Dish")}</Label>

        {dish ? (
          // The recipe is the dish. Its ingredients and steps are authored on
          // the recipe itself, so this is a link out rather than a second
          // editor (T-904). It opens in a new tab because the dialog is holding
          // an unsaved menu item.
          <div className="flex items-center justify-between gap-3 rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-3 py-2">
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-sm font-medium text-(--color-text-primary)">
                {dish.name}
              </span>
              <Link
                to="/recipes/$recipeId"
                params={{ recipeId: String(dish.recipeId) }}
                target="_blank"
                rel="noreferrer"
                className="flex w-fit items-center gap-1 text-xs text-(--color-action-fg) hover:underline"
              >
                {i18n._("View recipe")}
                <ExternalLink className="size-3" />
              </Link>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setDish(null)}>
              {i18n._("Change dish")}
            </Button>
          </div>
        ) : (
          <>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-text-secondary)" />
              <Input
                className="pl-9"
                placeholder={i18n._("Search plated recipes…")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {searchResults.length > 0 && (
              <div className="max-h-44 overflow-y-auto rounded-lg border border-(--color-border)">
                {searchResults.map((r) => {
                  // A recipe serves one menu item. One already on a different
                  // item is shown but cannot be picked; the item being edited
                  // keeps its own recipe.
                  const takenBy = r.menuItem && r.menuItem.id !== initial?.id ? r.menuItem : null;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      disabled={takenBy != null}
                      onClick={() => chooseDish(r.id, r.name, r.targetSellingPrice)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-(--color-surface-muted) disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-(--color-text-primary)">{r.name}</span>
                        {takenBy ? (
                          <span className="truncate text-xs text-(--color-text-secondary)">
                            {i18n._("Already on {name}", { name: takenBy.name })}
                          </span>
                        ) : null}
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        {r.cost?.costPerPortion != null && (
                          <span className="text-xs text-(--color-text-secondary)">
                            {i18n._("{cost} per plate", {
                              cost: formatCurrency(r.cost.costPerPortion),
                            })}
                          </span>
                        )}
                        {!takenBy && <Plus className="size-4 text-(--color-text-secondary)" />}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-(--color-text-secondary)">
              {i18n._("Only plated recipes can go on a menu. Build components inside the recipe as sub-recipes.")}
              {onCreatePlatedRecipe && (
                <>
                  {" "}
                  {i18n._("Don't see it?")}{" "}
                  <button
                    type="button"
                    onClick={onCreatePlatedRecipe}
                    className="font-medium text-(--color-action-fg) hover:underline"
                  >
                    {i18n._("Create a plated recipe")}
                  </button>
                </>
              )}
            </p>
          </>
        )}
      </div>
    </AppDialog>
  );
}
